import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import MapErrorBoundary from './MapErrorBoundary';
import MapLandmarkMarkers from './MapLandmarkMarkers';
import MapFirstToolbar from './MapFirstToolbar';
import { LANDMARK_META, MAP_FIRST_USP, landmarksFromProperty } from '../utils/mapLandmarks';
import { useMapLandmarks } from '../hooks/useMapLandmarks';
import { getHotelCoordinates } from '../utils/mapCoordinates';
import { LEAFLET_GOOGLE_STYLE_TILE } from '../config/mapConfig';
import 'leaflet/dist/leaflet.css';
import './NearbyPlacesMap.css';
import './MapFirstToolbar.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const ALL_TYPES = Object.keys(LANDMARK_META).filter((k) => k !== 'property');

const propertyPinIcon = L.divIcon({
  className: 'npm-property-pin-root',
  html: `<div class="npm-property-pin">${LANDMARK_META.property.emoji}</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

const MapFit = ({ points, fallback }) => {
  const map = useMap();
  useEffect(() => {
    map.invalidateSize({ animate: false });
    if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [32, 32], maxZoom: 15 });
    } else if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
    } else if (fallback) {
      map.setView([fallback.lat, fallback.lng], 13);
    }
  }, [map, points, fallback]);
  return null;
};

const NearbyPlacesMap = ({ property, height = 220, showToolbar = true }) => {
  const [activeCategories, setActiveCategories] = useState(() => new Set(ALL_TYPES));
  const coords = useMemo(() => getHotelCoordinates(property), [property]);
  const city = property?.city || property?.address || '';

  const { landmarks, loadingLandmarks } = useMapLandmarks({
    lat: coords?.lat,
    lng: coords?.lng,
    city: coords ? undefined : city,
    enabled: true,
    radius: 6000,
  });

  const propertyPlaces = useMemo(() => landmarksFromProperty(property), [property]);

  const toggleCategory = (key) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const center = coords || { lat: 20.5937, lng: 78.9629 };
  const mapPoints = useMemo(() => {
    const pts = [];
    if (coords) pts.push(coords);
    landmarks
      .filter((lm) => activeCategories.has(lm.type))
      .forEach((lm) => pts.push({ lat: lm.lat, lng: lm.lng }));
    return pts;
  }, [coords, landmarks, activeCategories]);

  return (
    <div className="nearby-places-map" style={{ '--npm-height': `${height}px` }}>
      {showToolbar && (
        <>
          <p className="nearby-places-map-usp">{MAP_FIRST_USP}</p>
          <MapFirstToolbar
            compact
            showViewToggle={false}
            activeCategories={activeCategories}
            onToggleCategory={toggleCategory}
            onShowAllCategories={() => setActiveCategories(new Set(ALL_TYPES))}
          />
        </>
      )}

      <div className="nearby-places-map-canvas">
        <MapErrorBoundary>
          <MapContainer
            center={[center.lat, center.lng]}
            zoom={13}
            className="nearby-places-leaflet"
            scrollWheelZoom={false}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer url={LEAFLET_GOOGLE_STYLE_TILE.url} attribution={LEAFLET_GOOGLE_STYLE_TILE.attribution} />
            <MapFit points={mapPoints} fallback={center} />
            {coords && (
              <Marker position={[coords.lat, coords.lng]} icon={propertyPinIcon} zIndexOffset={500}>
                <Popup>
                  <strong>{property?.name || 'Your stay'}</strong>
                </Popup>
              </Marker>
            )}
            <MapLandmarkMarkers
              lat={coords?.lat}
              lng={coords?.lng}
              city={coords ? undefined : city}
              activeCategories={activeCategories}
              radius={6000}
            />
          </MapContainer>
        </MapErrorBoundary>
        {loadingLandmarks && <div className="nearby-places-map-loading">Loading nearby places…</div>}
      </div>

      {propertyPlaces.length > 0 && (
        <ul className="nearby-places-map-legend">
          {propertyPlaces.slice(0, 6).map((place) => {
            const meta = LANDMARK_META[place.type] || LANDMARK_META.tourist;
            return (
              <li key={place.id}>
                <span className="npm-legend-emoji" aria-hidden>{meta.emoji}</span>
                <span>{place.name}</span>
                {place.distance != null && (
                  <span className="npm-legend-dist">
                    {place.distance}
                    {typeof place.distance === 'number' ? ' km' : ''}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default NearbyPlacesMap;
